import {BehaviorSubject, Observable, throwError as observableThrowError} from 'rxjs';
import {catchError, filter, mergeMap, take, tap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {ErrorHandlerService} from '../../../core/settings/error-handler.service';
import {AppConfig} from '../../../app.config';
import {GlobalSpinnerService} from '../../../core/spinner/global-spinner.service';
import {Injectable} from '@angular/core';
import {DPAMFile, GroupAttributeI, ProductSetupService, ViewLevel} from '../../services/product-setup.service';
import {AzureBlobStorageService} from '../../services/azure-blob-storage.service';

export interface DigitalFilesI {
  type: string;
  fileIds: number[] | null;
  mdmFileIds: number[];
  sizedFiles: Array<{
    type: string;
    fileIds: number[] | null;
    mdmFileIds: number[];
  }>;
}

export interface SpecificDigitalFileI {
  fileName: string;
  fileContent: string;
  fileSizeInKB: number;
  mimeType: string;
}

@Injectable({providedIn: 'root'})
export class DigitalFilesService {
  constructor(public http: HttpClient,
              public appConfig: AppConfig,
              public globalSpinner: GlobalSpinnerService,
              public errorService: ErrorHandlerService,
              private blobService: AzureBlobStorageService,
              private productService: ProductSetupService
  ) { }

  digitalFilesSource: BehaviorSubject<any>;
  digitalFiles$: Observable<any>;

  original = [
    {'type': 'DIGITALFILE_A_IMG_PRD'},
    {'type': 'DIGITALFILE_A_IMG_BRAND_LOGO'},
    {'type': 'DIGITALFILE_A_IMG_HDR'},
    {'type': 'DIGITALFILE_A_IMG_FTR'},
    {'type': 'DIGITALFILE_O_IMG_MEDIA'},
    {'type': 'DIGITALFILE_O_CARDAGRMNT'}
  ];

  final = [
    {'type': 'DIGITALFILE_A_IMG_PRD_300_190'},
    {'type': 'DIGITALFILE_A_IMG_PRD_200_283'},
    {'type': 'DIGITALFILE_A_IMG_PRD_1500_1500'},
    {'type': 'DIGITALFILE_A_IMG_HDR_670_123'},
    {'type': 'DIGITALFILE_A_IMG_FTR_670_91'},
    {'type': 'DIGITALFILE_A_IMG_PRD_1500_1500_IDS'},
    {'type': 'DIGITALFILE_A_IMG_BRAND_LOGO_600_380'},
    {'type': 'DIGITALFILE_A_IMG_BRAND_LOGO_EPS'},
   // {'type': 'DIGITALFILE_A_IMG_PRD_1500_1500_ECARD'},
    {'type': 'DIGITALFILE_D_IMG_MEDIA'},
    {'type': 'DIGITALFILE_A_CARDAGRMNT'}
  ];

  uploadImage(
    upcId: string,
    processInstanceId: number,
    fileType: 'final' | 'original',
    langIsoCode: string,
    imageType: string,
    file: File,
    query: string
  ): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    const link = `${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${upcId}/images/${fileType}/${langIsoCode}/${imageType}/${file.name}?${query}`;
    this.globalSpinner.startLoading();

    return this.blobService.uploadImage(query, file, `${upcId}/images/${fileType}/${langIsoCode}/${imageType}/${file.name}`)
      .pipe(
        tap(data => this.productService.loading$.next(data  === 100 ? null : data)),
        filter((data: number) => data === 100),
        take(1),
        mergeMap(() => {
          const productData = Object.assign({}, this.productService.productState.productData);
          productData.data = productData.data.map(p => {
            if (p.dpamRequest.processInstanceId === processInstanceId && p.dpamRequest.languageVersion.language.isoCode === langIsoCode) {
              const entity: DPAMFile = {
                fileContent: link,
                fileName: file.name,
                fileSizeInKB: Math.round(file.size / 1024),
                mimeType: file.type,
                type: imageType
              };
              switch (fileType) {
                case 'final':
                  p.dpamRequestFinalFiles.push(entity);
                  break;
                case 'original':
                  p.dpamRequestOriginalFiles.push(entity);
                  break;
              }
            }
            return p;
          });
          return this.productService.updateProduct(upcId, query, productData);
        }),
        mergeMap(() => this.productService.getProduct(upcId, query)),
        catchError((error: any) => this.handleError(error))
      );

  }

  uploadGroupProductImage(
    upcId: string,
    processInstanceId: number,
    fileType: 'final' | 'original',
    langIsoCode: string,
    imageType: string,
    file: File,
    query: string,
  ): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    const link = `${this.appConfig.data.accountLink}/${this.appConfig.data.containerName}/${upcId}/images/${fileType}/${langIsoCode}/${imageType}/${file.name}?${query}`;
    this.globalSpinner.startLoading();
    const productPath = this.productService.productState
      .groupData
      .data
      .find(p => p.request.processInstanceId === processInstanceId).dataPath;
    return this.blobService.uploadImage(query, file, `${upcId}/images/${fileType}/${langIsoCode}/${imageType}/${file.name}`)
      .pipe(
        tap(data => this.productService.loading$.next(data  === 100 ? null : data)),
        filter((data: number) => data === 100),
        take(1),
        mergeMap(() => this.productService.getProductByPath(productPath)),
        mergeMap((res) => {
          const productData = Object.assign({}, res);
          productData.data = productData.data.map(p => {
            if (p.dpamRequest.processInstanceId === processInstanceId) {
              const entity: DPAMFile = {
                fileContent: link,
                fileName: file.name,
                fileSizeInKB: Math.round(file.size / 1024),
                mimeType: file.type,
                type: imageType
              };
              switch (fileType) {
                case 'final':
                  p.dpamRequestFinalFiles.push(entity);
                  break;
                case 'original':
                  p.dpamRequestOriginalFiles.push(entity);
                  break;
              }
            }
            return p;
          });
          return this.productService.updateProductByPath(productPath, productData);
        }),
        mergeMap(() => this.productService.getSelectedProduct(productPath)),
        catchError((error: any) => this.handleError(error))
      );

  }


  deleteGroupProductImage(upcId: string,
              processInstanceId: number,
              fileType: 'final' | 'original',
              langIsoCode: string,
              imageType: string,
              fileName: string,
              query: string): Observable<any> {
    this.globalSpinner.startLoading();
    const productPath = this.productService.productState
      .groupData
      .data
      .find(p => p.request.processInstanceId === processInstanceId).dataPath;

    return this.blobService.deleteImage(query, `${upcId}/images/${fileType}/${langIsoCode}/${imageType}/${fileName}`)
      .pipe(
        mergeMap(() => this.productService.getProductByPath(productPath)),
        mergeMap((res) => {
          const productData = Object.assign({}, res);
          productData.data = productData.data.map(p => {
            if (p.dpamRequest.processInstanceId === processInstanceId && p.dpamRequest.languageVersion.language.isoCode === langIsoCode) {
              switch (fileType) {
                case 'final':
                  p.dpamRequestFinalFiles = p.dpamRequestFinalFiles.filter(f => !(f.fileName === fileName && f.type === imageType));
                  break;
                case 'original':
                  p.dpamRequestOriginalFiles = p.dpamRequestOriginalFiles.filter(f => !(f.fileName === fileName && f.type === imageType));
                  break;
              }
            }
            return p;
          });
          return this.productService.updateProductByPath(productPath, productData);
        }),
        catchError((error: any) => this.handleError(error))
      );
  }


  deleteImage(upcId: string,
              processInstanceId: number,
              fileType: 'final' | 'original',
              langIsoCode: string,
              imageType: string,
              fileName: string,
              query: string): Observable<any> {
    this.globalSpinner.startLoading();
    return this.blobService.deleteImage(query, `${upcId}/images/${fileType}/${langIsoCode}/${imageType}/${fileName}`)
      .pipe(
        mergeMap(() => {
          const productData = Object.assign({}, this.productService.productState.productData);
          productData.data = productData.data.map(p => {
            if (p.dpamRequest.processInstanceId === processInstanceId && p.dpamRequest.languageVersion.language.isoCode === langIsoCode) {
              switch (fileType) {
                case 'final':
                  p.dpamRequestFinalFiles = p.dpamRequestFinalFiles.filter(f => !(f.fileName === fileName && f.type === imageType));
                  break;
                case 'original':
                  p.dpamRequestOriginalFiles = p.dpamRequestOriginalFiles.filter(f => !(f.fileName === fileName && f.type === imageType));
                  break;
              }
            }
            return p;
          });
          return this.productService.updateProduct(upcId, query, productData);
        }),
        mergeMap(() => this.productService.getProduct(upcId, query)),
        catchError((error: any) => this.handleError(error))
      );
  }

  downloadImage(imgUrl, imgName) {
    this.http.get(imgUrl, {responseType: 'blob' as 'json'})
      .subscribe((res: any) => {
        const file = new Blob([res], {type: res.type});
        const blob = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = blob;
        link.download = imgName;

        // Version link.click() to work at firefox
        link.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        }));

        setTimeout(() => { // firefox
          window.URL.revokeObjectURL(blob);
          link.remove();
        }, 100);
      });
  }

  createNodesTree(
    digitalFiles: DPAMFile[],
    parentGroup: 'original' | 'final',
    fields: GroupAttributeI[],
    viewLevel: ViewLevel,
    chanel: string
  ) {
    let parent = this.getFields(parentGroup, chanel);

    if (fields && viewLevel === ViewLevel.Group) {
      parent = parent.filter((el: any) => !fields.some(field => field.mdmIdentifier === el.type));
    }
    if (fields && viewLevel === ViewLevel.Product) {
      parent = parent.filter((el: any) => fields.some(field => field.mdmIdentifier === el.type));
    }

    return parent.map(parentNode => {
      return {
        type: parentNode.type,
        item: digitalFiles.find(i => i.type === parentNode.type),
      };
    });
  }

  private getFields(parentGroup: 'original' | 'final', chanelType: string) {
    const excludedFields = [
  //    {'type': 'DIGITALFILE_A_IMG_PRD_1500_1500_ECARD'},
      {'type': 'DIGITALFILE_A_IMG_HDR_670_123'},
      {'type': 'DIGITALFILE_A_IMG_FTR_670_91'},
      {'type': 'DIGITALFILE_A_IMG_HDR'},
      {'type': 'DIGITALFILE_A_IMG_FTR'},
    ];

    const result = parentGroup === 'original' ? this.original : this.final;

    if (chanelType === 'PHYSICAL_SOLD_ONLINE') {
      return result.filter(item => excludedFields.every(ef => ef.type !== item.type));
    }

    return result;
  }

  handleResponse(res: any): any {
    this.globalSpinner.finishLoading();
    return res || {};
  }

  handleError(error: any) {
    this.globalSpinner.finishLoading();
    this.errorService.showError(error);
    return observableThrowError(error);
  }

}
